import { forwardRef } from 'react';
import { cn } from '../lib/utils';

const variantClasses = {
    default: '',
    primary:
        'text-white bg-blue-500 font-medium border border-blue-500 ' +
        'hover:brightness-90 data-[active=true]:brightness-[.85]',
    secondary: 'bg-[oklch(93.1%_0_none)] ' + 'hover:brightness-90 data-[active=true]:brightness-[.85]',
    destructive:
        'bg-crimson-500 text-white font-medium border border-crimson-500 ' +
        'hover:brightness-90 data-[active=true]:brightness-[.85]',
    ghost:
        'bg-transparent hover:bg-[oklch(98.1%_0_none)] hover:brightness-90 ' +
        'data-[active=true]:bg-[oklch(95.5%_0_none)] data-[active=true]:filter-none',
    outline:
        'border border-gray-light-700 text-gray-light-700 bg-transparent ' +
        'hover:brightness-90 ' +
        'data-[active=true]:bg-[oklch(95.5%_0_none)] data-[active=true]:filter-none',
    outline_destructive:
        'border border-crimson-500 text-crimson-500 font-medium bg-transparent ' +
        'hover:bg-[rgba(255,0,0,0.05)] hover:brightness-90 ' +
        'data-[active=true]:bg-[rgba(255,0,0,0.1)] data-[active=true]:filter-none',
    outline_secondary:
        'border border-[oklch(0.63_0_0)] text-[oklch(0.63_0_0)] font-medium bg-transparent ' +
        'hover:bg-[oklch(98.1%_0_none)] hover:brightness-90 ' +
        'data-[active=true]:bg-[oklch(95.5%_0_none)] data-[active=true]:filter-none',
    link: 'bg-transparent text-blue-500 underline hover:brightness-[.8] hover:no-underline',
};

const sizeClasses = {
    sm: 'text-xs leading-4 py-[0.3125rem] px-[0.5625rem]',
    md: 'text-sm leading-[0.875rem] p-[0.625rem]',
    lg: 'text-base leading-none py-4 px-5',
    full: 'w-full text-base leading-4 py-4 px-5',
};

const roundedClasses = {
    roundedXs: 'rounded-[0.5rem]',
    roundedSm: 'rounded-[0.75rem]',
    roundedMd: 'rounded-[1rem]',
    roundedLg: 'rounded-[1.25rem]',
    roundedFull: 'rounded-[6.25rem]',
    roundedNone: 'rounded-none',
};

const Button = forwardRef(function Button(
    {
        children,
        className,
        variant = 'secondary', // primary, secondary, destructive, outline, outline_destructive, outline_secondary, ghost, link
        size = 'lg', // sm, md, lg, full
        rounded = 'roundedMd', // roundedXs, roundedSm, roundedMd, roundedLg, roundedFull, roundedNone
        active = false,
        ...props
    },
    ref,
) {
    return (
        <button
            ref={ref}
            data-active={active || undefined}
            aria-pressed={active}
            className={cn(
                // base
                'cursor-pointer h-fit p-0 text-base leading-4',
                'transition-all duration-150 ease-in-out',
                'disabled:bg-[#aaaaaa] disabled:text-white',
                // variant (hover + active included per-variant)
                variantClasses[variant],
                // size overrides font + padding
                sizeClasses[size],
                // border-radius
                roundedClasses[rounded],
                // caller overrides (twMerge resolves conflicts)
                className,
            )}
            {...props}
        >
            {children}
        </button>
    );
});

export default Button;
